import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { combineLatest, Observable } from 'rxjs';
import { Client } from '../../core/entity/client';
import { getDateValidator } from 'src/app/core/entity/validation';
import { ValidationStatus } from 'src/app/core/entity/validationStatus';
import { ImportService } from '../../core/services/import.service';
import { TableColumn } from '../../shared/components/table/interfaces/table-column';
import { TodayDate } from '../../shared/misc/today.date';
import { ToastWithoutSeverity } from 'src/app/core/state/types';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';
import { UserSelectors } from 'src/app/core/state/users/users.selectors';
import { map } from 'rxjs/operators';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { FirmSelectors } from 'src/app/core/state/firm/firm.selectors';
import { isSweden } from 'src/app/core/entity/locale';
import { TranslatableStringType } from 'src/app/core/i18n/types';
import { toTitleMessage, toTextMessage } from 'src/app/core/entity/validationStrings';

enum ImportType {
  CLIENT = 'client',
  DEFERAL = 'deferal',
}

interface FileImportComponentTranslations {
  clients: TranslatableStringType;
  deferal: TranslatableStringType;
  name: TranslatableStringType;
  orgNumber: TranslatableStringType;
  address: TranslatableStringType;
  financialYearStart: TranslatableStringType;
  financialYearEnd: TranslatableStringType;
  enterStartDateForTasks: TranslatableStringType;
  fileformat: TranslatableStringType;
  importFileWrongFormatError: TranslatableStringType;
}

@Component({
  selector: 'app-file-import',
  templateUrl: './file-import.component.html',
  providers: [ImportService, getTranslationProvider('FileImportComponent')],
})
export class FileImportComponent extends TranslatableComponent<FileImportComponentTranslations> implements OnInit {
  @ViewChild('filechooser') fileChooser: ElementRef<HTMLInputElement>;

  @Input()
  set tabActive(active: boolean) {
    this.isVisible = active;
    this.hidePleaseWait = true;
    if (!this.isVisible) {
      this.filePath = '';
      this.selectedImportType = ImportType.CLIENT;
    }
  }

  public availableUsersWithoutAll$: Observable<SelectItem[]>;
  public ignoredImportsColumns$: Observable<TableColumn[]>;
  public filePath = '';
  public isVisible = false;

  hidePleaseWait = true;
  overWriteExistingOrgNumbers = false;
  clearPreviousDeferals = false;
  includeEmptyOrgNumbers = false;
  applyDefaulTemplates = false;
  applyClientResponsible = false;
  templateStartDate: string = new TodayDate().getTodayAsString();
  selectedUserId: number;
  selectedImportType: string = ImportType.CLIENT;
  ignoredImports: Client[] = [];
  importTypeSelectOptions$: Observable<{ label: string; value: ImportType }[]>;

  private i18n: FileImportComponentTranslations;

  get isClientImportSelected() {
    return this.selectedImportType === ImportType.CLIENT;
  }

  get isDeferalImportSelected() {
    return this.selectedImportType === ImportType.DEFERAL;
  }

  constructor(public importService: ImportService) {
    super();
    this.i18n = {} as FileImportComponentTranslations;
    this.importTypeSelectOptions$ = this.getImportTypeOptionsObservable();
    this.ignoredImportsColumns$ = this.getIgnoredImportsColumns();
  }

  override ngOnInit() {
    super.ngOnInit();

    this.availableUsersWithoutAll$ = this.store.select(UserSelectors.activeUsersWithoutAll).pipe(
      map((users) => {
        if (users.length) {
          this.selectedUserId = users[0].id;
        }

        return users.map(({ name, initials, id }) => ({ label: `${name} (${initials})`, value: id }));
      }),
    );
  }

  protected override onTranslationsLoaded(): void {
    this.i18n = {
      clients: this.translate('clients'),
      deferal: this.translate('deferal'),
      name: this.translate('name'),
      orgNumber: this.translate('orgNumber'),
      address: this.translate('address'),
      financialYearStart: this.translate('financialYearStart'),
      financialYearEnd: this.translate('financialYearEnd'),
      enterStartDateForTasks: this.translate('enterStartDateForTasks'),
      fileformat: this.translate('fileformat'),
      importFileWrongFormatError: this.translate('importFileWrongFormatError'),
    };
  }

  onFileChooserClick(): void {
    this.fileChooser.nativeElement.click();
  }

  changeListener(inputValue: any): void {
    if (inputValue && inputValue.files && inputValue.files.length > 0) {
      this.filePath = inputValue.files[0].name;
    } else {
      this.filePath = '';
    }
    this.ignoredImports = [];
  }

  startImport(inputValue: any): void {
    const { validate, min, max } = getDateValidator();
    const { status, title, text } = validate(this.templateStartDate, {
      min,
      max,
      label: this.i18n.enterStartDateForTasks,
    });
    const applyTemplates = this.applyDefaulTemplates;
    const hasValidationError = status !== ValidationStatus.Ok;

    if (this.isClientImportSelected && applyTemplates && hasValidationError) {
      const scope = 'validation';
      const summary = toTitleMessage(this.translateWithOtherScope(scope, title.key), title);
      const detail = toTextMessage(this.translateWithOtherScope(scope, text?.key), text);

      this.store.dispatch(ToastActions.showWarnMessage({ summary, detail }));
      return;
    }

    this.readThis(inputValue);
  }

  readThis(inputValue: any): void {
    const file: File = inputValue.files[0];
    const myReader: FileReader = new FileReader();

    const clientImportParameters = {
      includeEmptyOrgNumbers: this.includeEmptyOrgNumbers ? '1' : '0',
      overWriteExistingOrgNumbers: this.overWriteExistingOrgNumbers ? '1' : '0',
      clientResponsible: this.applyClientResponsible ? String(this.selectedUserId) : '0',
      templatesDate: this.applyDefaulTemplates ? this.templateStartDate : '0',
    };

    this.hidePleaseWait = false;

    myReader.onload = () => {
      let fileContent: string;
      try {
        // Validate the original bytes; encoding detection on decoded text is unreliable.
        fileContent = new TextDecoder('utf-8', { fatal: this.isClientImportSelected }).decode(
          myReader.result as ArrayBuffer,
        );
      } catch {
        this.showErrorMessage({
          summary: this.i18n.fileformat,
          detail: this.i18n.importFileWrongFormatError,
        });
        this.hidePleaseWait = true;
        return;
      }

      if (this.isDeferalImportSelected) {
        this.importDeferal(fileContent, this.clearPreviousDeferals);
        return;
      }

      this.importClients(fileContent, clientImportParameters);
    };

    myReader.onerror = () => this.showImportFailedMessage();
    myReader.readAsArrayBuffer(file);
  }

  private getImportTypeOptionsObservable() {
    return combineLatest([this.translationsLoaded$, this.store.select(FirmSelectors.selectCountryCode)]).pipe(
      map(([translations, countryCode]) => {
        if (!isSweden(countryCode)) {
          return [{ label: translations.clients, value: ImportType.CLIENT }];
        }
        return [
          { label: translations.clients, value: ImportType.CLIENT },
          { label: translations.deferal, value: ImportType.DEFERAL },
        ];
      }),
    );
  }

  private getIgnoredImportsColumns() {
    return this.translationsLoaded$.pipe(
      map((translations) => {
        const toSafeString = (translationKey: keyof FileImportComponentTranslations) =>
          translations[translationKey] || '';

        return [
          { field: 'name', header: toSafeString('name'), sortable: false },
          { field: 'corporateIdentity', header: toSafeString('orgNumber'), sortable: false },
          { field: 'address', header: toSafeString('address'), sortable: false },
          { field: 'currentFinancialYear.span.start', header: toSafeString('financialYearStart'), sortable: false },
          { field: 'currentFinancialYear.span.end', header: toSafeString('financialYearEnd'), sortable: false },
        ];
      }),
    );
  }

  private toLocalDateFormat(date: string): string {
    return date ? new TodayDate(this.getActiveLanguage()).formatDateByLocale(new Date(date)) : '';
  }

  private setIgnoredImports(event: any) {
    this.hidePleaseWait = true;

    try {
      let data;
      if (Array.isArray(event)) {
        data = event;
      } else {
        data = JSON.parse(event._body);
      }
      this.ignoredImports = data.map((row: any) => {
        const { start, end } = row.currentFinancialYear.span ?? {};
        const formatDate = (date: string) => this.toLocalDateFormat(date);
        row.currentFinancialYear.span.start = formatDate(start);
        row.currentFinancialYear.span.end = formatDate(end);
        return row;
      });
    } catch {
      this.showErrorMessage({
        summary: this.translate('unknownErrorOccurred'),
        detail: this.translate('importCompletedError'),
      });
    }

    const extraMessage =
      this.ignoredImports.length > 0
        ? this.translate('ignoredImportCountMessage', { params: { ignoredImportCount: this.ignoredImports.length } })
        : this.translate('allClientsImported');
    this.showInfoMessage({ summary: this.translate('importCompleted'), detail: extraMessage });
  }

  private importClients(
    body: string,
    importParameters: {
      includeEmptyOrgNumbers: string;
      overWriteExistingOrgNumbers: string;
      clientResponsible: string;
      templatesDate: string;
    },
  ) {
    this.importService.sendFileContentToImport(body, importParameters).subscribe(
      (ignored: any[]) => {
        if (ignored?.length > 0) {
          this.setIgnoredImports(ignored);
        }
        this.showInfoMessage({
          summary: this.translate('importCompleted'),
          detail: this.translate('clientsHaveBeenImported'),
        });
        this.hidePleaseWait = true;
      },
      () => this.showImportFailedMessage(),
    );
  }

  private importDeferal(body: string, clearPreviousDeferals: boolean) {
    this.importService.sendFileContentToDeferalImport(body, clearPreviousDeferals).subscribe(
      () => {
        this.showInfoMessage({
          summary: this.translate('importCompleted'),
          detail: this.translate('deferalsHaveBeenImported'),
        });
        this.hidePleaseWait = true;
      },
      () => this.showImportFailedMessage(),
    );
  }

  private showInfoMessage(config: ToastWithoutSeverity) {
    this.store.dispatch(ToastActions.showInfoMessage(config));
  }

  private showErrorMessage(config: ToastWithoutSeverity) {
    this.store.dispatch(ToastActions.showErrorMessage(config));
  }

  private showImportFailedMessage() {
    this.showErrorMessage({
      summary: this.translate('importInterrupted'),
      detail: this.translate('importCouldNotBeCompleted'),
    });
    this.hidePleaseWait = true;
  }
}
